import React, { memo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LayoutRectangle, StyleSheet } from 'react-native';

import { useLinkTo, useNavigation } from '@react-navigation/native';

import CheckmarkIcon from '@/assets/icons/checkmark-promo';
import ClockIcon from '@/assets/icons/clock-promo';
import { Button, ButtonProps } from '@/components/ButtonComponent';
import { SizedBox } from '@/components/SizedBox';
import { Text } from '@/components/TextComponent';
import { Box, Image, Row, TouchableBox } from '@/components/lib/components';
import { ds } from '@/components/lib/design.system';
import { BetrAnalytics } from '@/feature/analytics';
import { AnalyticsEvent } from '@/feature/analytics/constants';
import { useUserStore } from '@/hooks/use-user-store';
import { common } from '@/styles/styles';
import { isIos } from '@/utils/constants-platform-specific';
import { formatPromoExpirationTime } from '@/utils/renderDate';

import { usePromoTheme } from '../hooks/use-promo-theme';
import { usePromotionsNavigation } from '../hooks/use-promotions-navigation';
import { usePromotionsStore } from '../hooks/use-promotions-store';
import { usePromoOptIn } from '../hooks/use-submissions';
import { Promotion } from '../types';
import { GlowShape } from './OvalGlow';

type Props = {
    mode: 'lobby' | 'promohub';
    promo: Promotion;
    cardWidth?: number;
};
type PrimaryButtonVariant = 'CUSTOM_CTA' | 'OPTED_IN' | 'OPT_IN';

const CARD_CONFIG = {
    dynamic: {
        imageSize: { width: 148, height: 148 },
        imageSeparator: 12,
        titleSeparator: 20,
        titleNumberOfLines: undefined,
        titleFontSize: 20,
        titleLetterSpacing: -0.8,
        shouldShowExpiration: true,
        titleLineHeight: 24,
        buttonSize: 's' as ButtonProps['size'],
        buttonHorizontalPadding: 14,
        buttonVerticalPadding: 8,
        optedInButtonBorderWidth: 2,
        iconSize: 20,
    },
    fixed: {
        imageSize: { width: 88, height: 120 },
        imageSeparator: 8,
        titleSeparator: 16,
        titleNumberOfLines: 2,
        titleFontSize: 18,
        titleLetterSpacing: -0.54,
        shouldShowExpiration: false,
        titleLineHeight: 22,
        buttonSize: 'xs' as ButtonProps['size'],
        buttonHorizontalPadding: 10,
        buttonVerticalPadding: 4,
        optedInButtonBorderWidth: 1.75,
        iconSize: 16,
    },
} as const;

const getCardSizingConfig = (dynamicHeight?: boolean) => {
    return dynamicHeight ? CARD_CONFIG.dynamic : CARD_CONFIG.fixed;
};

export const PromoCard = memo(({ promo, mode, cardWidth }: Props) => {
    const [cardSize, setCardSize] = useState<LayoutRectangle>({ width: cardWidth ?? 0, height: 0, x: 0, y: 0 });
    const isDisplayedFromPromohub = mode === 'promohub';
    const {
        imageSize,
        imageSeparator,
        titleSeparator,
        titleNumberOfLines,
        titleFontSize,
        titleLetterSpacing,
        shouldShowExpiration,
        titleLineHeight,
        buttonSize,
        buttonHorizontalPadding,
        buttonVerticalPadding,
        optedInButtonBorderWidth,
        iconSize,
    } = getCardSizingConfig(isDisplayedFromPromohub);
    const { t } = useTranslation(['promohub']);
    const { goToPromo } = usePromotionsNavigation();
    const locallyOptedInPromos = usePromotionsStore(state => state.locallyOptedInPromos);
    const navigation = useNavigation();
    const bonusEnginePromoId = promo.bonusEngineDetails?.id ?? undefined;
    const showOptInButton = promo.bonusEngineDetails?.optInRequired ?? promo.data.display_cta;
    const redirectPath = promo.data.redirect_path ?? undefined;
    const secondaryButton = promo.data.secondary_cta ?? undefined;
    const customCta = promo.data.custom_cta ?? undefined;
    const customCtaPath = promo.data.custom_cta_path ?? undefined;
    const showCustomCta = !!customCta && !!customCtaPath;

    const theme = promo.data.promo_theme.data;
    const expirationTime = promo.bonusEngineDetails?.expirationTime;
    const hasOptedIn = locallyOptedInPromos.includes(promo.uid) || promo.optedIn;

    const linkTo = useLinkTo();

    // Locally store opted-in promo IDs to keep them visible (with opted-in status)
    // in the "Available" tab until the user switches tabs, click on Promo Details or leaves the screen.
    const addLocallyOptedInPromo = usePromotionsStore(state => state.actions.handleLocalOptIn);
    const handleLocalOptIn = usePromotionsStore(state => state.actions.handleLocalOptIn);

    const onLocalOptIn = useCallback(
        (promoId: string) => {
            isDisplayedFromPromohub ? addLocallyOptedInPromo(promoId) : handleLocalOptIn(promoId);
        },
        [addLocallyOptedInPromo, handleLocalOptIn, isDisplayedFromPromohub]
    );

    const imageUri = isDisplayedFromPromohub ? promo.data.promocard_image_square.url ?? '' : promo.data.image.url ?? '';

    const { optIn, loading } = usePromoOptIn({
        promoId: promo.uid,
        segmented: promo.data.segmented,
        redirectPath,
        onSuccess: () => {
            onLocalOptIn?.(promo.uid);
        },
        bonusEnginePromoId,
        analyticsEvent: mode === 'lobby' ? AnalyticsEvent.OPT_IN_CAROUSEL : AnalyticsEvent.OPT_IN_PROMOS_SCREEN,
    });

    const {
        background_color,
        promo_title_color,
        top_glow_color,
        expiration_tag_color,
        primary_button_background_color,
        primary_button_text_color,
        secondary_button_text_color,
    } = usePromoTheme(theme);
    const paddingHorizontal = showOptInButton ? buttonHorizontalPadding : 0;

    const onCustomCtaPress = useCallback(() => {
        try {
            linkTo(`/${customCtaPath}`);
        } catch (error) {
            console.error('Error navigating to custom CTA path:', error);
        }
    }, [customCtaPath, linkTo]);

    const guest = useUserStore(state => state.guest);
    const onCardPress = useCallback(() => {
        if (guest) {
            navigation.reset({ routes: [{ name: 'Launch' }] });
            return;
        }
        BetrAnalytics.trackProductEvent(
            mode === 'lobby'
                ? AnalyticsEvent.OPEN_PROMO_DETAILS_FROM_LOBBY
                : AnalyticsEvent.OPEN_PROMO_DETAILS_FROM_PROMOHUB,
            {
                promoId: promo.uid,
            }
        );
        goToPromo(promo.uid, promo.data.external_url);
    }, [guest, mode, promo.uid, promo.data.external_url, goToPromo, navigation]);

    const primaryButtonVariant: PrimaryButtonVariant = showCustomCta
        ? 'CUSTOM_CTA'
        : hasOptedIn
        ? 'OPTED_IN'
        : 'OPT_IN';

    const renderCustomCtaButton = () => (
        <Button
            label={
                <Text
                    variant={buttonSize === 's' ? 'titleSmall' : 'bodySmaller'}
                    style={{ color: primary_button_text_color }}
                    fontWeight={'600'}
                    letterSpacing={buttonSize === 's' ? -0.08 : -0.23}
                >
                    {customCta}
                </Text>
            }
            hierarchy={'tertiary'}
            hitSlop={8}
            size={buttonSize}
            shape={'rect'}
            style={{
                paddingHorizontal: buttonHorizontalPadding,
                paddingVertical: buttonVerticalPadding,
                backgroundColor: primary_button_background_color,
                ...styles.button,
            }}
            onPress={onCustomCtaPress}
        />
    );

    const renderOptedInButton = () => (
        <Button
            label={
                <Row gap={isDisplayedFromPromohub ? 's8' : 's4'} alignItems={'center'}>
                    <Text variant={'titleSmall'} style={{ color: secondary_button_text_color }}>
                        {t('opted_in')}
                    </Text>
                    <CheckmarkIcon width={iconSize} height={iconSize} color={primary_button_background_color} />
                </Row>
            }
            size={buttonSize}
            hierarchy={'tertiary'}
            shape={'rect'}
            style={{
                borderColor: primary_button_background_color,
                borderWidth: optedInButtonBorderWidth,
                paddingHorizontal: buttonHorizontalPadding - optedInButtonBorderWidth,
                paddingVertical: buttonVerticalPadding - optedInButtonBorderWidth,
                ...styles.button,
            }}
            disabled={true}
        />
    );

    const renderOptInButton = () => (
        <Button
            label={
                <Text
                    variant={buttonSize === 's' ? 'titleSmall' : 'bodySmaller'}
                    style={{ color: primary_button_text_color }}
                    fontWeight={'600'}
                    letterSpacing={buttonSize === 's' ? -0.08 : -0.23}
                >
                    {showOptInButton ? t('opt_in') : secondaryButton ?? t('learn_more')}
                </Text>
            }
            testID={`promo-card-primary-cta-${promo.uid}`}
            variant={'dark'}
            hitSlop={8}
            size={buttonSize}
            shape={'rect'}
            style={{
                paddingHorizontal: buttonHorizontalPadding,
                paddingVertical: buttonVerticalPadding,
                backgroundColor: primary_button_background_color,
                ...styles.button,
            }}
            disabled={false}
            loading={loading}
            onPress={showOptInButton ? optIn : onCardPress}
        />
    );

    return (
        <TouchableBox
            flex={1}
            flexDirection={'row'}
            onPress={onCardPress}
            activeOpacity={0.7}
            style={{ backgroundColor: background_color, ...common.borderCurve }}
            borderRadius={'r20'}
            overflow={'hidden'}
            onLayout={event => setCardSize(event.nativeEvent.layout)}
        >
            {cardSize.width > 0 ? (
                <GlowShape
                    x={cardSize.width - (isDisplayedFromPromohub ? 92 : 90)}
                    y={isDisplayedFromPromohub ? -80 : -122}
                    width={200}
                    height={200}
                    color={top_glow_color}
                    opacity={0.9}
                    blur={isDisplayedFromPromohub ? 60 : 55}
                />
            ) : null}
            <Box style={styles.border} />
            <Box paddingVertical={'s16'} pl={'s16'} flex={1}>
                <Box flex={1} justifyContent={'space-between'}>
                    <Text
                        style={[styles.title, { color: promo_title_color }]}
                        fontSize={titleFontSize}
                        lineHeight={titleLineHeight}
                        letterSpacing={titleLetterSpacing}
                        numberOfLines={titleNumberOfLines}
                    >
                        {promo.data.title ?? ''}
                    </Text>
                    {expirationTime && shouldShowExpiration ? (
                        <Row alignItems={'center'} gap={'s4'} mt={'s8'}>
                            <ClockIcon color={expiration_tag_color} />
                            <Box flex={1}>
                                <Text
                                    style={{ color: expiration_tag_color }}
                                    numberOfLines={1}
                                    ellipsizeMode={'tail'}
                                    variant={'labelLarge'}
                                >
                                    {formatPromoExpirationTime(expirationTime, t)}
                                </Text>
                            </Box>
                        </Row>
                    ) : null}
                    <SizedBox value={titleSeparator} />
                    <Row gap={showOptInButton || showCustomCta ? 's4' : 's0'}>
                        {primaryButtonVariant === 'CUSTOM_CTA' && renderCustomCtaButton()}
                        {primaryButtonVariant === 'OPTED_IN' && renderOptedInButton()}
                        {primaryButtonVariant === 'OPT_IN' && renderOptInButton()}
                        {showOptInButton ? (
                            <Button
                                label={
                                    <Text
                                        variant={buttonSize === 's' ? 'titleSmall' : 'bodySmaller'}
                                        style={{ color: secondary_button_text_color }}
                                        fontWeight={isDisplayedFromPromohub ? '600' : '700'}
                                    >
                                        {secondaryButton ?? t('learn_more')}
                                    </Text>
                                }
                                onPress={onCardPress}
                                size={buttonSize}
                                shape={'rect'}
                                style={[
                                    common.backgroundColorTransparent,
                                    {
                                        //if there's only one button, remove the padding so it's properly aligned to the left
                                        paddingHorizontal,
                                        paddingVertical: buttonVerticalPadding,
                                    },
                                ]}
                                hierarchy={'tertiary'}
                            />
                        ) : null}
                    </Row>
                </Box>
            </Box>
            <SizedBox value={imageSeparator} />
            <Image
                source={{ uri: imageUri }}
                resizeMode={'contain'}
                style={[styles.image, { ...imageSize }, isDisplayedFromPromohub ? styles.imageRight : null]}
            />
        </TouchableBox>
    );
});

const styles = StyleSheet.create({
    image: {
        alignSelf: 'center',
    },
    imageRight: {
        right: -32,
    },
    title: {
        fontFamily: 'gamay-extrabold',
        textTransform: 'uppercase',
        // on android, the custom font breaks if we add a fontWeight
        fontWeight: isIos ? '800' : undefined,
    },
    button: {
        borderCurve: 'continuous',
    },
    border: {
        position: 'absolute',
        zIndex: 1,
        width: '100%',
        height: '100%',
        borderRadius: 20,
        borderWidth: 1,
        borderColor: ds.colorValue.border.subtle,
        pointerEvents: 'none',
    },
});
